<template>
  <div
    id="rs-sidebar-menu"
    class="rs-sidebar-menu"
    :class="sidebarClass"
    :style="[{ width: sidebarWidth }]"
  >
    <div
      class="sidebar-menu__header"
    >
      <div
        v-if="!collapsed"
        class="d-flex justify-content-between mt-3 mx-2"
      >
        <div>
          <RouterLink
            id="main_Dashboard"
            :to="{
              name: 'Dashboard',
              params: { ...$route.params },
            }"
            exact
            class="d-block"
          >
            <img
              src="/img/rentsync_logo.png"
              class="sidebar-menu__header-logo"
            >
          </RouterLink>
          <div
            v-if="$env && !['production', 'demo'].includes($env.VUE_APP_ENV)"
            class="small text-white"
          >
            (env: {{ $utils.capitalize($env.VUE_APP_ENV) }})
          </div>
        </div>
        <div>
          <RsNavigationButton
            id="previous"
            icon="arrow-left"
            variant="nav"
            @click="toggleCollapse"
          />
        </div>
      </div>
      <div
        v-else
        class="d-flex flex-column align-items-center mt-3 mx-2"
      >
        <div>
          <img
            src="/img/rentsync_symbol.svg"
            class="sidebar-menu__header-logo-sm"
          >
        </div>
        <RsNavigationButton
          id="previous"
          icon="arrow-right"
          variant="nav"
          class="sidebar-menu__header-arrow-right"
          @click="toggleCollapse"
        />
      </div>
    </div>
    <div class="flex-fill h-100 main-nav-container pb-5">
      <slot name="navigation" />
    </div>
    <div>
      <slot name="navigation-floor" />
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import appMixin from '@/mixins/app-mixin';
import { IRouteGroup } from '@/shared/interfaces/route-group';

export default defineComponent({
  name: 'RsPartialAppSidebar',

  mixins: [
    appMixin,
  ],

  data() {
    return {
      collapsed: window.screen.width <= 1280 as boolean,
      windowWidth: window.screen.width as number,
      width: '225px' as string,
      widthCollapsed: '76px' as string,
      expandedGroup: {} as IRouteGroup,
      collapsedDropdownTopPos: 0 as number,
      collapsedDropdownLeftPos: 0 as number,
      isCollapsedDropdownShowing: false as boolean,
      navParentItemOnHover: '' as string,
      floatingHeight: '' as string,
      floatingScrollable: false as boolean,
    };
  },
  computed: {
    sidebarWidth(): string {
      return this.collapsed ? this.widthCollapsed : this.width;
    },
    sidebarClass(): Record<string, boolean> {
      return {
        'slide-enter-active': true,
        'sidebar-collapsed': this.collapsed,
      };
    },
    dropdownStyle(): Record<string, string> {
      let height: number = 200;
      let routeHeight: number = 0;
      if (this.collapsed && Object.keys(this.expandedGroup).length) {
        const { length } = this.getGroupChildren(this.expandedGroup);
        routeHeight = length * 40;
      }
      if (routeHeight < 200) {
        height = routeHeight;
        this.floatingScrollable = false;
      } else {
        height = 200;
        this.floatingScrollable = true;
      }
      this.floatingHeight = `${height}px`;
      return {
        top: `${this.collapsedDropdownTopPos}px`,
        left: `${this.collapsedDropdownLeftPos}px`,
        height: `${height}px`,
      };
    },
  },
  watch: {
    windowWidth(): void {
      if (this.windowWidth <= 1280) {
        this.collapsed = true;
      } else {
        this.collapsed = false;
      }
      this.$emit('on-collapse-toggle', this.collapsed);
    },
  },
  mounted() {
    this.onResize();
    window.addEventListener('resize', this.onResize);
    this.formattedRouteNames = { properties: 'Properties', websites: 'Websites', leads: 'Leads', ad_syndication: 'Listing Management' };
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
  methods: {
    handleSubnavMouseEnter(group:IRouteGroup): void {
      if (group) this.navParentItemOnHover = group.basePath;
    },
    handleSubnavMouseLeave(): void {
      this.navParentItemOnHover = '';
    },
    checkIsRouteGroupHovered(groupName: string): boolean {
      return this.navParentItemOnHover === groupName;
    },
    handleSidebarCollapse(collapsed: boolean): void {
      this.collapsed = !!collapsed;
    },
    onResize(): void {
      this.windowWidth = window.innerWidth;
      this.hideFloatingDropdown();
    },
    resetRoute(): void {
      this.navParentItemOnHover = '';
    },
    checkIsExpandedPath(basePath: string): boolean {
      return this.expandedPath.startsWith(basePath);
    },
    getExpandedGroup(group:IRouteGroup, event: Event): void {
      this.expandedGroup = group;
      this.expandedPath = !this.collapsed && this.expandedPath.includes(group.basePath) ? '' : group.basePath;
      const childEls = ['span', 'i'];
      const el = event.target as HTMLElement;
      let top;

      if (childEls.includes(el.tagName.toLowerCase())) top = el!.parentElement!.getBoundingClientRect().top;
      else top = el.getBoundingClientRect().top;

      this.collapsedDropdownTopPos = top - 7;
      this.collapsedDropdownLeftPos = 68;

      if (this.expandedGroup) this.isCollapsedDropdownShowing = true;
    },
    checkIsFormattedRoute(name: string): boolean {
      return !this.collapsed && this.getFormattedRoute(name).includes('Management');
    },
    toggleCollapse(): void {
      this.collapsed = !this.collapsed;
      this.$emit('on-collapse-toggle', this.collapsed);
    },

    expand(): void {
      this.collapsed = false;
      this.$emit('on-collapse-toggle', this.collapsed);
    },

    hideFloatingDropdown(): void {
      if (this.isCollapsedDropdownShowing) {
        this.isCollapsedDropdownShowing = false;
        this.floatingScrollable = false;
      }
    },
  },

});
</script>
<style lang="scss" scoped>
@import '@/styles/_variables';
$collapsed-width: 69px;
$expanded-width: 225px;

.rs-sidebar-menu {
  background-color: $secondary-color-dark;
  font-family: $body-font-family;
  font-weight: bold;
  display:flex;
  flex-direction: column;
  height: 100vh;
  z-index: 0;

  &.sidebar-collapsed {
    z-index: 1;
  }

  .main-nav-container {
    &::-webkit-scrollbar {
      display: none;
    }
    overflow-y: auto;
  }

  .sidebar-menu {
    &__header {
      &-logo {
        width: 120px;
        &-sm {
          width: 32px;
          height: 16px;
        }
      }
      &-arrow-right {
        padding-top:15px;
      }
    }
  }

  .fa-chevron-down {
    font-size: 12px;
    font-weight: 700;
  }

  .top-level-nav-item-avatar {
    width: 40px;
    height: 40px;
  }
  .list-group {

    .list-group-item {
      background-color: $secondary-color-dark;

      .btn-accordion, a {
        .fal {
          font-weight: 400;
        }
        font-size: 16px;
        font-weight: bold;
        color: $white;

        .item-icon {
          color: $tertiary-muted;
          &--expanded {
            margin-right:0.65rem;
            font-weight:500;
          }
        }
      }

      span.router-link-active,
      a.router-link-active,
      .btn-accordion.router-link-active .btn-accordion-nav-text,
      .btn-accordion-nav-text {
        i {
          color: $primary-color;
        }
      }

      .btn-accordion-nav-text--expanded {
        width: 175px;
      }

      .router-link-active {
        span {
          color: $white;
        }
      }
    }
  }

  .text-size-collapsed {
    font-size: 10px;
  }

  .item-title-collapsed {
    line-height: 1.22;
    margin-top: 2px;
  }

  .sidebar-menu {
    &__body {
      margin-top: 15px;
      height:400px;
    }
    &__footer {
      font-size: 16px;
    }
  }

  .collapsed-dropdown {
    position:absolute;
    background-color: $secondary-color-dark;
    z-index: 1;
    border-left: solid $dark-gray 0.5px;
    border-top-right-radius: 5px!important;
    border-bottom-right-radius: 5px!important;
    &-list {
      width: 245px!important;
    }
  }

  .expanded-max-width {
    max-width: $expanded-width;
  }

  .collapsed-max-width {
    max-width: $collapsed-width;
  }

  .font-size-24 {
    font-size: 24px;
  }

  .font-size-16 {
    font-size: 16px;
  }

  .padding-top-2 {
    padding-top: 2px;
  }

  .active-icon-color {
    color: $primary-color!important;
  }

  .active-text-color {
    color: $white!important;
  }
  .active-bg-color {
    background: rgba(255, 255, 255, 0.05);
  }
  .list-group-item {
    &:hover {
      background: rgba(255, 255, 255, 0.05);
    }
  }

  .item-greyed-text {
    color: $tertiary-muted;
  }

}

 .slide-enter-active {
    -moz-transition-duration: 0.3s;
    -webkit-transition-duration: 0.3s;
    -o-transition-duration: 0.3s;
    transition-duration: 0.3s;
    -moz-transition-timing-function: ease;
    -webkit-transition-timing-function: ease;
    -o-transition-timing-function: ease;
    transition-timing-function: ease;
  }

  .slide-leave-active {
    -moz-transition-duration: 0.3s;
    -webkit-transition-duration: 0.3s;
    -o-transition-duration: 0.3s;
    transition-duration: 0.3s;
    -moz-transition-timing-function: ease;
    -webkit-transition-timing-function: ease;
    -o-transition-timing-function: ease;
    transition-timing-function: ease;
  }

  .slide-enter-to, .slide-leave {
    max-height: 300px;
    overflow: hidden;
  }

  .slide-enter, .slide-leave-to {
    overflow: hidden;
    max-height: 0;
  }
</style>
